import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";

import { StatusCode } from "api/protocols";
import { GetFacilityStatus, GetFacilityStatusInputs } from "api/rpc/2024-04/facilityAdmin/facility/status";

import { showError } from "redux/actions/ui";
import { TFacilityStatusInput } from "redux/reducers/models/facility";
import { formatDate } from "helpers/Helpers";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { range } from "lodash";

import DatePickerInput from "components/datePickerInput/DatePickerInput";
import Input from "components/form/input/Input";
import SelectNew from "components/select/SelectNew";
import Sheet from "components/sheet/Sheet";
import Spin from "components/spin/spin";
import Portal from "elements/Portal";
import Checkbox from "components/form/checkbox/Checkbox";
import FormLayout from "components/form/FormLayout";
import CopyLink from "components/copyLink/CopyLink";
import { useHistory } from "react-router";
import MessageDisplay from "components/messageDisplay/MessageDisplay";
export interface IFacilityStatusState {
  date: Date;
  inputData: Array<{ status_input_id: number; value: string }>;
}

type TFaciltyStatus = {
  state: { isOpen: boolean } & IFacilityStatusState;
  update: (newState: IFacilityStatusState) => void;
  close: () => void;
  onOk: () => void;
};

export default function StatusModal(props: TFaciltyStatus) {
  const { state, update, close, onOk } = props;
  const { t } = useTranslation();
  const { facility } = useAppSelector(store => store.facilityStore);
  const history = useHistory(); 
  const dispatch = useAppDispatch();

  const [statusInputs, setStatusInputs] = useState<TFacilityStatusInput[]>(undefined);

  useEffect(() => {
    const source = axios.CancelToken.source();

    if (state.isOpen) {
      void loadFacilityStatus(source.token);
    }

    if (state.isOpen && statusInputs === undefined) {
      void loadStatusInputs(source.token);
    }
    return () => source.cancel();
  }, [state.isOpen, state.date]);

  async function loadFacilityStatus(token?: CancelToken) {
    if (state.inputData !== undefined) {
      update({ ...state, inputData: undefined });
    }
    const res = await GetFacilityStatus({ date: formatDate(state.date) }, token ? false : true, token);

    console.log("facility status ", res);

    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting facility status inputs.")));
      update({ ...state, inputData: [] });
    }

    const usableStructure = res.data.map(val => ({ status_input_id: val.status_input_id, value: val.value }));
    // setFacilityStatus(res.data.length > 0 ? usableStructure : []);
    console.log("updated state ... ", usableStructure);

    update({ ...state, inputData: usableStructure });
  }

  async function loadStatusInputs(token?: CancelToken) {
    const res = await GetFacilityStatusInputs(null, token ? false : true, token);

    console.log("facility status inputs ", res);

    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting facility status inputs.")));
    }

    setStatusInputs(res.data);
  }

  const changeModalDate = (selectedDate: Date) => update({ ...state, date: selectedDate });

  const updateFacilityStatus = (statusId: number, selected: string) => {
    if (state.inputData.find(val => val.status_input_id === statusId)) {
      // update prev status input
      update({
        ...state,
        inputData: state.inputData.map(val => {
          if (val.status_input_id === statusId) {
            return {
              ...val,
              value: selected,
            };
          } else {
            return val;
          }
        }),
      });
    } else {
      // add new status input
      update({ ...state, inputData: [...state.inputData, { status_input_id: statusId, value: selected }] });
    }
  };

  function getInputRowRange() {
    if (!statusInputs) {
      return [];
    }

    let maxRow = 0;

    statusInputs.forEach(input => {
      if (input.row > maxRow) {
        maxRow = input.row;
      }
    });

    
    return range(1, maxRow + 1, 1);
  }

  return (
    <Portal isMounted={state.isOpen}>
      <Sheet
        open={state.isOpen}
        size="medium"
        closable
        onCancel={() => close()}
        onOk={onOk}
        title={t("Daily Status")}
        okText={t("Save")}
        cancelText={t("Close")}
      >
        <div className="pb-4">
          <DatePickerInput months={1} position="left" startingDate={state.date} setStartingDate={changeModalDate} />
        </div>
        {state.inputData === undefined ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <div style={{ height: "50px" }}>
              <Spin />
            </div>
          </div>
        ) : statusInputs?.length > 0 ? (
          <>
            <FormLayout>
              {getInputRowRange().map(row => {
                const rowInputs = statusInputs.filter(input => input.row === row);

                return (
                  <FormLayout.Group key={row}>
                    {rowInputs?.map(rowStatusInput => {
                      const rowStatusInputData = state.inputData.find(
                        dataInput => dataInput.status_input_id === rowStatusInput.id,
                      );

                      if (!rowStatusInput) {
                        return null;
                      } else if (rowStatusInput.type === "select") {
                        return (
                          <div key={rowStatusInput.id} className={rowStatusInput.required ? " required" : ""}>
                            <SelectNew
                              key={rowStatusInput.id}
                              id={`select_${rowStatusInput.id}`}
                              value={rowStatusInputData?.value}
                              onChange={val => updateFacilityStatus(rowStatusInput.id, val)}
                              options={
                                rowStatusInput.values ? rowStatusInput.values.map(val => ({ id: val, label: val })) : []
                              }
                              label={rowStatusInput.label}
                              helpText={rowStatusInput.help_text}
                            />
                          </div>
                        );
                      } else if (rowStatusInput.type === "input") {
                        return (
                          <div key={rowStatusInput.id} className={rowStatusInput.required ? " required" : ""}>
                            <Input
                              label={rowStatusInput.label}
                              value={rowStatusInputData?.value}
                              onChange={e => updateFacilityStatus(rowStatusInput.id, e.target.value)}
                              helpText={rowStatusInput.help_text}
                            />
                          </div>
                        );
                      } else if (rowStatusInput.type === "checkbox") {
                        return (
                          <div key={rowStatusInput.id} className={rowStatusInput.required ? " required" : ""}>
                            <Checkbox
                              key={rowStatusInput.id}
                              value={rowStatusInputData?.value ?? "false"}
                              checked={rowStatusInputData?.value === "true"}
                              size="large"
                              onChange={e =>
                                updateFacilityStatus(rowStatusInput.id, e.target.checked ? "true" : "false")
                              }
                              label={rowStatusInput.label}
                              helpText={rowStatusInput.help_text}
                            />
                          </div>
                        );
                      }
                    })}
                  </FormLayout.Group>
                );
              })}
            </FormLayout>
          
            <div>
              <CopyLink
                label={t("Embed Calendar Link")}
                link={window.origin + `/embed/calendar/${facility?.short_name}`}
              />
            </div>
          </>
        ) : (
          <>
            <MessageDisplay
              title={t("No Status Inputs Found")}
              icon={"circle-exclamation"}
              content={t("No status inputs have been found. To add status inputs, please go to settings.")}
              actions={[
                {
                  label: t("+ Add Inputs"),
                  action: () => history.push("/admin/settings/status-inputs/"),
                },
              ]}
            />
          </>
        )
        }
      </Sheet>
    </Portal>
  );
}
